/**
 * Client-side prep for homework photos before the presigned PUT: resize the
 * long edge, re-encode to JPEG and reject what can't be uploaded. Spec
 * `docs/design/design_handoff_note_templates/21-anh-bai-ve-nha.md` §1 — phone
 * photos run 4-12 MB and are never uploaded raw.
 */

/** Long-edge resize, extracted from `compressToJpeg` so the math is testable
 *  without a canvas. Never upscales; preserves aspect ratio; rounds to
 *  integers. */
export function fitWithin(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const longestEdge = Math.max(width, height);
  if (longestEdge <= maxEdge) {
    return { width: Math.round(width), height: Math.round(height) };
  }
  const scale = maxEdge / longestEdge;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * Re-encodes `file` as JPEG, resized to fit within `maxEdge` on its long
 * edge. Uses `createImageBitmap(file, { imageOrientation: 'from-image' })` so
 * portrait phone photos read their EXIF orientation instead of landing
 * sideways (canvas otherwise ignores it) — `bitmap.width`/`height` are read
 * only after that option applies.
 *
 * Never returns a blob larger than the input file: if the JPEG re-encode
 * comes out bigger (already-compressed source, e.g. a small screenshot),
 * the original file is returned as the blob, with the bitmap's
 * (orientation-corrected) dimensions.
 */
export async function compressToJpeg(
  file: File,
  opts?: { maxEdge?: number; quality?: number },
): Promise<{ blob: Blob; width: number; height: number }> {
  const maxEdge = opts?.maxEdge ?? 2000;
  const quality = opts?.quality ?? 0.85;

  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const bitmapWidth = bitmap.width;
  const bitmapHeight = bitmap.height;
  const { width, height } = fitWithin(bitmapWidth, bitmapHeight, maxEdge);

  let blob: Blob;
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('imageCompress: 2D canvas context unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);
    blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('imageCompress: 2D canvas context unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);
    blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) resolve(result);
          else reject(new Error('imageCompress: canvas.toBlob failed'));
        },
        'image/jpeg',
        quality,
      );
    });
  }
  bitmap.close();

  // Only a JPEG/PNG original may stand in for its re-encode: any other type
  // (WebP, GIF) would be presigned as image/jpeg and fail the pinned
  // content-type signature (review I4).
  if (blob.size > file.size && (file.type === 'image/jpeg' || file.type === 'image/png')) {
    return { blob: file, width: bitmapWidth, height: bitmapHeight };
  }
  return { blob, width, height };
}

/**
 * Returns a reason key the caller localises through `notes.photos.failToast`
 * (`{{name}} không tải được: {{reason}}`), or `null` when `file` is
 * acceptable. iOS transcodes HEIC for `<input type="file">`; a HEIC/HEIF file
 * that still arrives (Android gallery, desktop drag) is rejected rather than
 * pulling in a HEIC decoder.
 */
export function rejectReason(file: File, maxBytes: number): 'heic' | 'notImage' | 'tooLarge' | null {
  const type = file.type.toLowerCase();
  if (type === 'image/heic' || type === 'image/heif') return 'heic';
  if (!type.startsWith('image/')) return 'notImage';
  if (file.size > maxBytes) return 'tooLarge';
  return null;
}
